/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { displayPhoneNumber, formatAccountNumber, displayAccountNumber } from '../../utils/formatUtils';
import PhoneInput from '../shared/PhoneInput';
import NotificationPreferencesDialog from '../shared/NotificationPreferencesDialog';
import { useTimeFormat, formatTime } from '../../utils/timeFormat';
import '../../styles/UserProfile.css';

const UserProfile = () => {
  const { user, setUser } = useAuth();
  const timeFormat = useTimeFormat();
  const [savingTimeFormat, setSavingTimeFormat] = useState(false);

  // Applies straight away — there is nothing else to fill in, so no Save step.
  const handleTimeFormatChange = async (next) => {
    if (next === timeFormat || savingTimeFormat) return;
    setSavingTimeFormat(true);
    try {
      const { data } = await axios.put('/api/users/time-format', { time_format: next });
      setUser(prev => (prev ? { ...prev, time_format: data.time_format } : prev));
      toast.success(next === '24h' ? 'Times now shown as 24-hour' : 'Times now shown as 12-hour (AM/PM)');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save time format');
    } finally {
      setSavingTimeFormat(false);
    }
  };
  const [profileData, setProfileData] = useState({});
  const [departments, setDepartments] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  // Accounts created through the NCSU ID sign-in have no password of ours to
  // change — NC State holds their credentials. Keyed off whether a password
  // actually exists rather than auth_provider, because signing in once with an
  // NCSU ID flips an existing local account to 'oidc' while its password (and
  // the ability to change it) still works.
  const canChangePassword = profileData.has_password !== false;
  // Signed in with a temporary password from "Forgot password?" - open the
  // form and ask for a new one. SignIn passes the flag in navigation state;
  // the profile's must_change_password covers a reload or a later visit.
  const location = useLocation();
  const navigate = useNavigate();
  const mustChangePassword = Boolean(profileData.must_change_password || location.state?.mustChangePassword);
  const passwordCardRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchUserProfile();
    fetchDepartments();
    fetchFacilities();
  }, []);

  useEffect(() => {
    if (!loading && mustChangePassword && canChangePassword) {
      setShowPasswordForm(true);
      // The form sits at the bottom of the page.
      setTimeout(() => passwordCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    }
  }, [loading, mustChangePassword, canChangePassword]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/api/users/profile');
      const userData = response.data;
      
      // Ensure account_numbers is always an array
      if (!userData.account_numbers) {
        userData.account_numbers = [];
      }
      
      setProfileData(userData);
      setEditData({
        ...userData,
        facility: userData.facility?._id || userData.facility || ''
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile data');
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/api/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ── Billing person (Invoice To) ──
  // Users are their own billing person unless they explicitly nominate someone.
  const billingContact = {
    is_self: editData.billing_contact?.is_self !== false,
    name: editData.billing_contact?.name || '',
    email: editData.billing_contact?.email || '',
    contact_number: editData.billing_contact?.contact_number || ''
  };

  // What "myself" resolves to, from the fields currently being edited.
  const selfBillingContact = {
    name: `${editData.first_name || ''} ${editData.last_name || ''}`.trim(),
    email: editData.email || '',
    contact_number: editData.contact_number || ''
  };

  // What is actually on file, for the read-only view.
  const savedBillingContact = (() => {
    const bc = profileData.billing_contact;
    const isSelf = bc?.is_self !== false;
    if (isSelf) {
      return {
        is_self: true,
        name: `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim(),
        email: profileData.email || '',
        contact_number: profileData.contact_number || ''
      };
    }
    return {
      is_self: false,
      name: bc.name || '',
      email: bc.email || '',
      contact_number: bc.contact_number || ''
    };
  })();

  const handleBillingContactChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      billing_contact: {
        ...(prev.billing_contact || {}),
        is_self: false,
        [field]: value
      }
    }));
  };

  const handleBillingSelfToggle = (checked) => {
    setEditData(prev => ({
      ...prev,
      billing_contact: checked
        ? { is_self: true, name: '', email: '', contact_number: '' }
        : { ...(prev.billing_contact || {}), is_self: false }
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateProfileForm = () => {
    const errors = {};
    
    // Check required fields
    if (!editData.first_name || !editData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!editData.last_name || !editData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    
    if (!editData.department) {
      errors.department = 'Department is required';
    }

    // A nominated billing person needs at least a name and a valid email so the
    // business office has someone reachable to contact.
    const bc = editData.billing_contact;
    if (bc && bc.is_self === false) {
      if (!bc.name || !bc.name.trim()) {
        errors.billing_name = 'Billing person name is required';
      }
      if (!bc.email || !bc.email.trim()) {
        errors.billing_email = 'Billing person email is required';
      } else if (!/.+@.+\..+/.test(bc.email.trim())) {
        errors.billing_email = 'Enter a valid billing person email';
      }
    }

    return errors;
  };

  const scrollToField = (fieldName) => {
    // Try to find the field by various methods
    const element = document.querySelector(`[name="${fieldName}"]`) ||
                    document.querySelector(`#${fieldName}`) ||
                    document.querySelector(`[data-field="${fieldName}"]`);
    
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
      
      // Add a highlight animation
      element.style.transition = 'all 0.3s ease';
      element.style.transform = 'scale(1.02)';
      setTimeout(() => {
        element.style.transform = 'scale(1)';
      }, 300);
    }
  };

  const handleSaveProfile = async () => {
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate form
    const errors = validateProfileForm();
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Show error toast with first error
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      scrollToField(firstErrorField);
      
      return;
    }
    
    setSaving(true);
    try {
      // Filter out empty account numbers
      const filteredAccountNumbers = editData.account_numbers?.filter(
        account => account.number && account.number.trim() !== ''
      ) || [];
      
      const updateData = {
        ...editData,
        account_numbers: filteredAccountNumbers
      };
      
      
      const response = await axios.put('/api/users/profile', updateData);
      
      
      // Ensure account_numbers is always an array in the response
      if (!response.data.account_numbers) {
        response.data.account_numbers = [];
      }
      
      setProfileData(response.data);
      setUser(response.data); // Update auth context
      setEditing(false);
      setValidationErrors({});
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    // Any character that isn't a letter or digit - a short whitelist here used
    // to reject passwords like "Summer_2026" or "Plant-Lab+1" that sign-up
    // accepts.
    const hasSpecialChar = /[^A-Za-z0-9]/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push(`At least ${minLength} characters`);
    if (!hasUpperCase) errors.push('One uppercase letter');
    if (!hasLowerCase) errors.push('One lowercase letter');
    if (!hasNumbers) errors.push('One number');
    if (!hasSpecialChar) errors.push('One special character');

    return errors;
  };

  const handleChangePassword = async () => {
    // Validation checks
    if (!passwordData.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }

    if (!passwordData.newPassword) {
      toast.error('Please enter a new password');
      return;
    }

    if (!passwordData.confirmPassword) {
      toast.error('Please confirm your new password');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    // Password strength validation
    const passwordErrors = validatePassword(passwordData.newPassword);
    if (passwordErrors.length > 0) {
      toast.error(`Password must include: ${passwordErrors.join(', ')}`);
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setSaving(true);
    try {
      await axios.put('/api/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
      if (mustChangePassword) {
        setProfileData(prev => ({ ...prev, must_change_password: false }));
        // Lifts ProtectedRoute's hold on the rest of the app.
        setUser(prev => (prev ? { ...prev, must_change_password: false } : prev));
        navigate(location.pathname, { replace: true, state: null });
      }
      toast.success('Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    // Ensure account_numbers is always an array when canceling
    const dataWithAccountNumbers = {
      ...profileData,
      account_numbers: profileData.account_numbers || []
    };
    setEditData(dataWithAccountNumbers);
    setEditing(false);
  };

  const cancelPasswordChange = () => {
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordForm(false);
  };

  const formatAccountNumbers = (accountNumbers) => {
    if (!accountNumbers || accountNumbers.length === 0) {
      return 'No account numbers on file';
    }
    return accountNumbers.map(acc => `${displayAccountNumber(acc.number)} (${acc.source})`).join(', ');
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-header-info">
          <h1>{profileData.first_name} {profileData.last_name}</h1>
          <p className="profile-email">{profileData.email}</p>
          <div className="profile-badges">
            <span className={`badge ${profileData.status?.toLowerCase()}`}>
              {profileData.status}
            </span>
          </div>
        </div>
        <div className="profile-actions">
          {!editing && (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>
              Edit Profile
            </button>
          )}
          {canChangePassword && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowPasswordForm(mustChangePassword || !showPasswordForm)}
            >
              Change Password
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => setShowNotifPrefs(true)}
          >
            Notification Preferences
          </button>
        </div>
      </div>

      {/* Password Change Form - at the top so it can't be missed, above all
          the profile details. */}
      {showPasswordForm && canChangePassword && (
        <div className="profile-card password-card" ref={passwordCardRef}>
          <h2>Change Password</h2>
          {mustChangePassword && (
            <div className="password-change-notice">
              You signed in with a temporary password. Choose a new password now - the temporary
              one expires 24 hours after it was sent, and the rest of the site stays locked until
              you do. Enter the temporary password from your email as your current password.
            </div>
          )}
          <div className="password-form">
            <div className="form-group">
              <label>{mustChangePassword ? 'Current (temporary) Password *' : 'Current Password *'}</label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                placeholder={mustChangePassword ? 'Enter the temporary password from your email' : 'Enter your current password'}
                required
              />
            </div>
            <div className="form-group">
              <label>New Password *</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="Enter new password"
                required
              />
              <small className="password-requirements">
                Password must include: 8+ characters, uppercase, lowercase, number, special character (e.g. ! @ # - _ +)
              </small>
              {/* Shown here as well as in the toast, which is easy to miss -
                  a password that fails these is never sent. */}
              {passwordData.newPassword && validatePassword(passwordData.newPassword).length > 0 && (
                <small className="password-mismatch">
                  Still needed: {validatePassword(passwordData.newPassword).join(', ')}
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Confirm New Password *</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="Confirm your new password"
                required
              />
              {passwordData.newPassword && passwordData.confirmPassword && 
               passwordData.newPassword !== passwordData.confirmPassword && (
                <small className="password-mismatch">Passwords do not match</small>
              )}
            </div>
            <div className="form-actions">
              <button 
                className="btn btn-primary" 
                onClick={handleChangePassword}
                disabled={saving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              >
                {saving ? 'Changing...' : 'Change Password'}
              </button>
              {!mustChangePassword && (
                <button className="btn btn-secondary" onClick={cancelPasswordChange}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="profile-content">
        <div className="profile-row-split">
        {/* Personal Information Card */}
        <div className="profile-card">
          <h2>Personal Information</h2>
          {editing ? (
            <div className="edit-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    data-field="first_name"
                    value={editData.first_name || ''}
                    onChange={(e) => handleEditChange('first_name', e.target.value)}
                    placeholder="First Name"
                    className={validationErrors.first_name ? 'error' : ''}
                  />
                  {validationErrors.first_name && (
                    <small className="error-message">{validationErrors.first_name}</small>
                  )}
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    data-field="last_name"
                    value={editData.last_name || ''}
                    onChange={(e) => handleEditChange('last_name', e.target.value)}
                    placeholder="Last Name"
                    className={validationErrors.last_name ? 'error' : ''}
                  />
                  {validationErrors.last_name && (
                    <small className="error-message">{validationErrors.last_name}</small>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editData.email || ''}
                  disabled
                  className="disabled"
                  title="Email cannot be changed"
                />
              </div>

              <div className="form-group">
                <label>Contact Number</label>
                <PhoneInput
                  value={editData.contact_number || ''}
                  onChange={(v) => handleEditChange('contact_number', v)}
                />
              </div>

              <div className="form-group">
                <label>Company/Organization</label>
                <input
                  type="text"
                  name="company"
                  data-field="company"
                  value={editData.company || ''}
                  onChange={(e) => handleEditChange('company', e.target.value)}
                  placeholder="Company or Organization"
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={editData.address || ''}
                  onChange={(e) => handleEditChange('address', e.target.value)}
                  placeholder="Full Address"
                  rows={3}
                />
              </div>

              {profileData.is_ncsu && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Unity ID</label>
                    <input
                      type="text"
                      name="unity_id"
                      data-field="unity_id"
                      value={editData.unity_id || ''}
                      onChange={(e) => handleEditChange('unity_id', e.target.value)}
                      placeholder="Unity ID"
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Department *</label>
                <select
                  name="department"
                  data-field="department"
                  value={editData.department?._id || editData.department || ''}
                  onChange={(e) => handleEditChange('department', e.target.value)}
                  className={validationErrors.department ? 'error' : ''}
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept._id} value={dept._id}>
                      {dept.department_name}
                    </option>
                  ))}
                </select>
                {validationErrors.department && (
                  <small className="error-message">{validationErrors.department}</small>
                )}
              </div>

              <div className="form-group">
                <label>Facility {profileData.institution_roles?.some(r => r.role === 'Admin') ? '(Optional)' : ''}</label>
                <select
                  value={editData.facility || ''}
                  onChange={(e) => handleEditChange('facility', e.target.value)}
                  className="form-select"
                >
                  <option value="">Not Disclosed</option>
                  {facilities.map((facility) => (
                    <option key={facility._id} value={facility._id}>
                      {facility.facility_name}
                    </option>
                  ))}
                </select>
                {profileData.institution_roles?.some(r => r.role === 'Admin') && (
                  <small className="form-helper-text">Pre-selects this facility in filters across admin pages</small>
                )}
              </div>

            </div>
          ) : (
            <div className="profile-info">
              <div className="info-row">
                <span className="info-label">Unity ID:</span>
                <span className="info-value">{profileData.unity_id || 'NA'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Company:</span>
                <span className="info-value">{profileData.company || 'Not provided'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Department:</span>
                <span className="info-value">{profileData.department?.department_name || 'Not assigned'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Contact:</span>
                <span className="info-value">{displayPhoneNumber(profileData.contact_number)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Address:</span>
                <span className="info-value">{profileData.address || 'Not provided'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Facility:</span>
                <span className="info-value">
                  {profileData.facility?.facility_name || 'Not Disclosed'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Billing Person (Invoice To) Card */}
        <div className="profile-card">
          <h2>Billing Person (Invoice To)</h2>
          <p className="card-intro">
            The person who handles your invoice payments and accounts. They take no part in the
            invoicing process &mdash; they are only listed on the billing request sent to the
            business office, so the business office can contact them instead of you.
          </p>
          {editing ? (
            <div className="edit-form">
              <div className="billing-self-toggle">
                <input
                  type="checkbox"
                  id="billing-is-self"
                  checked={billingContact.is_self}
                  onChange={(e) => handleBillingSelfToggle(e.target.checked)}
                />
                <label htmlFor="billing-is-self">
                  I am my own billing person
                </label>
              </div>

              {billingContact.is_self ? (
                <div className="profile-info">
                  <div className="info-row">
                    <span className="info-label">Name:</span>
                    <span className="info-value">{selfBillingContact.name || 'Not provided'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{selfBillingContact.email || 'Not provided'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Contact:</span>
                    <span className="info-value">
                      {displayPhoneNumber(selfBillingContact.contact_number)}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Billing Person Name</label>
                    <input
                      type="text"
                      value={billingContact.name}
                      onChange={(e) => handleBillingContactChange('name', e.target.value)}
                      placeholder="Full name"
                      className={validationErrors.billing_name ? 'error' : ''}
                    />
                    {validationErrors.billing_name && (
                      <small className="error-message">{validationErrors.billing_name}</small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Billing Person Email</label>
                    <input
                      type="email"
                      value={billingContact.email}
                      onChange={(e) => handleBillingContactChange('email', e.target.value)}
                      placeholder="name@example.com"
                      className={validationErrors.billing_email ? 'error' : ''}
                    />
                    {validationErrors.billing_email && (
                      <small className="error-message">{validationErrors.billing_email}</small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Billing Person Contact Number</label>
                    <PhoneInput
                      value={billingContact.contact_number}
                      onChange={(v) => handleBillingContactChange('contact_number', v)}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="profile-info">
              <div className="info-row">
                <span className="info-label">Name:</span>
                <span className="info-value">
                  {savedBillingContact.name || 'Not provided'}
                  {savedBillingContact.is_self ? ' (you)' : ''}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{savedBillingContact.email || 'Not provided'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Contact:</span>
                <span className="info-value">
                  {displayPhoneNumber(savedBillingContact.contact_number)}
                </span>
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Account Numbers Card */}
        <div className="profile-card">
          <h2>Account Numbers</h2>
          {editing ? (
            <div className="account-edit-mode">
              <div className="account-list">
                {(editData.account_numbers || []).map((account, index) => (
                  <div key={index} className="account-edit-item">
                    <div className="account-inputs">
                      <input
                        type="text"
                        placeholder="XXXXXX-XXXXX"
                        value={account.number || ''}
                        onChange={(e) => {
                          const newAccounts = [...(editData.account_numbers || [])];
                          newAccounts[index].number = formatAccountNumber(e.target.value);
                          setEditData({...editData, account_numbers: newAccounts});
                        }}
                        maxLength={12}
                      />
                      <input
                        type="text"
                        placeholder="Source"
                        value={account.source || ''}
                        onChange={(e) => {
                          const newAccounts = [...(editData.account_numbers || [])];
                          newAccounts[index].source = e.target.value;
                          setEditData({...editData, account_numbers: newAccounts});
                        }}
                      />
                    </div>
                    <button 
                      type="button"
                      className="btn-remove"
                      onClick={() => {
                        const newAccounts = (editData.account_numbers || []).filter((_, i) => i !== index);
                        setEditData({...editData, account_numbers: newAccounts});
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button 
                type="button"
                className="btn btn-add-account"
                onClick={() => {
                  const newAccounts = [...(editData.account_numbers || []), {number: '', source: ''}];
                  setEditData({...editData, account_numbers: newAccounts});
                }}
              >
                + Add Account Number
              </button>
            </div>
          ) : (
            <div className="account-numbers">
              {profileData.account_numbers && profileData.account_numbers.length > 0 ? (
                <div className="account-list">
                  {profileData.account_numbers.map((account, index) => (
                    <div key={index} className="account-item">
                      <span className="account-number">{displayAccountNumber(account.number)}</span>
                      <span className="account-source">{account.source}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-accounts">
                  <p>No account numbers on file</p>
                  <small>Edit your profile to add account numbers</small>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Display Settings Card */}
        <div className="profile-card">
          <h2>Display Settings</h2>
          <fieldset className="time-format-setting" disabled={savingTimeFormat}>
            <legend className="info-label">Time format</legend>
            <p className="time-format-hint">
              How times are shown and entered everywhere in the app.
            </p>
            <div className="time-format-options">
              {[
                { value: '12h', label: '12-hour (AM/PM)', example: '2:30 PM' },
                { value: '24h', label: '24-hour (military)', example: '14:30' }
              ].map(option => (
                <label
                  key={option.value}
                  className={`time-format-option ${timeFormat === option.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="time_format"
                    value={option.value}
                    checked={timeFormat === option.value}
                    onChange={() => handleTimeFormatChange(option.value)}
                  />
                  <span className="time-format-option-label">{option.label}</span>
                  <span className="time-format-option-example">e.g. {option.example}</span>
                </label>
              ))}
            </div>
            <p className="time-format-now">Right now: <strong>{formatTime(new Date())}</strong></p>
          </fieldset>
        </div>

        {/* Save/Cancel buttons for edit mode */}
        {editing && (
          <div className="profile-card">
            <div className="form-actions">
              <button 
                className="btn btn-primary" 
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="btn btn-secondary" onClick={cancelEdit}>
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>

      <NotificationPreferencesDialog
        open={showNotifPrefs}
        onClose={() => setShowNotifPrefs(false)}
      />
    </div>
  );
};

export default UserProfile;

